"use client"

import { useState } from "react"
import { CheckCircle} from "lucide-react"
import Translate from "../../assets/translate.svg"

export default function Join() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    major: "",
    learn: "",
    skill: "",
    hobbies: "",
    hobbyMessage: "",
    ideaMessage: "",
    newsletter: true,
  })
  const [status, setStatus] = useState({ sending: false, done: false, error: "" })
  const [emailValid, setEmailValid] = useState(true)
  const [arabic, setArabic] = useState(false)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setForm((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }))

    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      setEmailValid(emailRegex.test(value))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate required fields
    if (!form.name.trim() || !form.email.trim() || !form.major.trim()) {
      setStatus({ sending: false, done: false, error: arabic ? "يرجى إدخال الاسم والبريد الإلكتروني والتخصص." : "Please enter your name, email, and major." })
      return
    }

    if (!emailValid) {
      setStatus({ sending: false, done: false, error: arabic ? "يرجى إدخال بريد إلكتروني صحيح." : "Please enter a valid email address." })
      return
    }

    setStatus({ sending: true, done: false, error: "" })

    try {
      const params = new URLSearchParams()
      Object.entries(form).forEach(([key, value]) => params.append(key, value))

      const url = `https://script.google.com/macros/s/AKfycby8NL1hCqncKwTzAP4JAgfk8fhiwJyIaradZFG6iOlr1Zocv-VqAlZSJjEA_vgIiDzAyg/exec?${params.toString()}`
      const res = await fetch(url, { method: "GET", redirect: "follow" })
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`)

      let json
      try { json = await res.json() } catch {
        setStatus({ sending: false, done: true, error: "" })
        setForm({ name: "", email: "", major: "", learn: "", skill: "", hobbies: "", hobbyMessage: "", ideaMessage: "", newsletter: true })
        return
      }

      if (json.status === "success" || res.status === 200) {
        setStatus({ sending: false, done: true, error: "" })
        setForm({ name: "", email: "", major: "", learn: "", skill: "", hobbies: "", hobbyMessage: "", ideaMessage: "", newsletter: true })
      } else throw new Error(json.message || "Unknown error")
    } catch (err) {
      console.error("[v0] Submission error:", err)
      setStatus({ sending: false, done: false, error: arabic ? "فشل الإرسال — حاول مرة أخرى." : "Submission failed — try again." })
    }
  }

  const t = (en, ar) => (arabic ? ar : en) // simple translation helper

  if (status.done) {
    return (
      <section
        id="join"
        className="py-12 px-4 min-h-screen flex items-center justify-center bg-gradient-to-b from-[#f3f3f1] to-[#ffffff]"
      >
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="w-20 h-20 text-green-600 animate-bounce" />
          </div>
          <h2 className="text-4xl font-bold mb-3 text-gray-800">{t("You're In!", "تم التسجيل!")}</h2>
          <p className="text-lg text-gray-600 mb-2">{t("Welcome to Interlink", "مرحبا بك في إنترلينك")}</p>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">{t(
            "We've sent you a confirmation email. Check your inbox and get ready to connect with amazing people building great things together.",
            "لقد أرسلنا لك بريدًا إلكترونيًا للتأكيد. تحقق من صندوق الوارد الخاص بك واستعد للتواصل مع أشخاص رائعين يبنون أشياء عظيمة معًا."
          )}</p>

          <button
            onClick={() => setStatus({ sending: false, done: false, error: "" })}
            className="bg-green-600 hover:bg-green-700 text-white py-3 px-8 rounded-lg font-medium transition-colors"
          >
            {t("Register Another Person", "تسجيل شخص آخر")}
          </button>
        </div>
      </section>
    )
  }

  return (
    <section id="join" className="py-12 px-4 min-h-screen bg-gradient-to-b from-[#eae9e4] to-[#ffffff]">
        <div className="max-w-2xl mx-auto bg-white p-8 rounded-lg shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-3xl font-bold text-gray-800">{t("Join Interlink", "انضم إلى إنترلينك")}</h2>
            <button
              onClick={() => setArabic(!arabic)}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              title={t("Translate to Arabic", "ترجمة إلى العربية")}
            >
              <img src={Translate} alt="" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-6">{t("Hi, tell us who you are and what you want to build/learn.", "أخبرنا من أنت وما الذي تريد بناءه/تعلمه")}</p>

          <div className="flex flex-col gap-4">
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t("Full name", "الاسم الكامل")}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder={t("Gmail address", "البريد الإلكتروني")}
              className={`p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                emailValid ? "border-gray-300 focus:ring-blue-500" : "border-red-500 focus:ring-red-500"
              }`}
            />
            <h2 className="text-gray-700 pl-3">I Study</h2>
            <input
              name="major"
              value={form.major}
              onChange={handleChange}
              placeholder={t("Major (and what part you enjoy)", "التخصص (وما الجزء الذي تحبه)")}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className=" w-full flex flex-col gap-3"> 
            <input
              name="learn"
              value={form.learn}
              onChange={handleChange}
              placeholder={t("What do you want to learn from your major?", "ماذا تريد أن تتعلم من تخصصك؟")}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <input
              name="skill"
              value={form.skill}
              onChange={handleChange}
              placeholder={t("A skill you have or want to learn", "مهارة لديك أو تريد تعلمها")}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <h2 className="text-gray-700 pl-3">I Love To</h2>
            <input
              name="hobbies"
              value={form.hobbies}
              onChange={handleChange}
              placeholder={t("Hobbies (reading, writing, sport...)", "الهوايات (قراءة، كتابة، رياضة...)")}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            
            <textarea
              name="ideaMessage"
              value={form.ideaMessage}
              onChange={handleChange}
              placeholder={t(
                "Anything you want to try, learn, or build — or a message to the club",
                "أي شيء تريد تجربته أو تعلمه أو بناؤه — أو رسالة للنادي"
              )}
              className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="4"
            />
              </div>

            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="newsletter"
                checked={form.newsletter}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">{t("Subscribe to Interlink updates", "الاشتراك في تحديثات إنترلينك")}</span>
            </label>

            {status.error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg border border-red-200">{status.error}</div>
            )}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={status.sending}
              className="mt-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white py-3 px-6 rounded-lg font-medium transition-colors"
            >
              {status.sending ? t("Submitting...", "جارٍ الإرسال...") : t("Register", "تسجيل")}
            </button>
          </div>
        </div>
    </section>
  )
}
